import type { Router, NavigationGuard } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

/**
 * Guardas de navegación.
 *
 * Se registran por separado de la instancia del router para poder probarlas sin
 * arrancar la aplicación: el store de auth se resuelve en el momento de la
 * navegación, cuando Pinia ya está instalado.
 */
export function registerAuthGuards(router: Router): Router {
  const beforeEach: NavigationGuard = async (to, _from, next) => {
    const authStore = useAuthStore()

    // La restauración de la sesión es asíncrona: al recargar la página el token
    // está en el almacenamiento pero el usuario todavía no se ha pedido. Sin
    // esperar aquí, `isAuthenticated` es falso porque falta `user` y toda ruta
    // protegida manda al login —que es exactamente el síntoma de que recargar
    // echaba del sistema.
    await authStore.whenReady()

    if (to.meta.requiresAuth) {
      if (!authStore.isAuthenticated) {
        return next({ name: 'login', query: { redirect: to.fullPath } })
      }

      const requiredRoles = to.meta.roles as string[] | undefined
      if (requiredRoles?.length && !authStore.hasAnyRole(requiredRoles)) {
        return next({ name: 'home' })
      }

      // Permiso exigido por la ruta. Vive junto al de roles porque algunos
      // permisos se comparten entre roles con distinto nombre —el panel de
      // marketing lo ven el marketing y el admin—, y preguntar por el permiso
      // es preguntar por lo que la persona puede hacer de verdad.
      const requiredPermission = to.meta.permission as string | undefined
      if (requiredPermission !== undefined && !authStore.hasPermission(requiredPermission)) {
        return next({ name: 'home' })
      }
    }

    if (to.name === 'login' && authStore.isAuthenticated) {
      // Cada quien a su pantalla: el admin al panel de dirección, quien solo
      // mira marketing al de marketing, y el asesor al inicio. Mandar a todos
      // al mismo sitio dejaba al de marketing en una página sin nada suyo.
      return next({
        name: authStore.canAccessDashboard
          ? 'dashboard'
          : authStore.canAccessMarketing
            ? 'marketing'
            : 'home',
      })
    }

    next()
  }

  router.beforeEach(beforeEach)
  return router
}